import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { getSetupStatus, saveBrand, LOGO_URL } from "../lib/api";
import { suggestPalette, contrast, onColor } from "../lib/brand";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };
const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});

// The company's own brand. It shows in the app (sidebar, login, window title, colours) and on generated social
// images (logo, colours, website). Admins can change it any time.
export default function BrandSettings({ isAdmin }) {
  const [brand, setBrand] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [suggested, setSuggested] = useState([]);

  const load = () => getSetupStatus().then(b => {
    setBrand(b);
    setForm({ company_name: b.company_name || "", website: b.website || "", primary_color: b.primary_color || "#1e3a8a", accent_color: b.accent_color || "#f97316" });
  });
  useEffect(() => { load().catch(() => toast.error("Could not load the brand")); }, []);
  if (!brand) return null;

  const save = async (extra = {}) => {
    setBusy(true);
    try { setBrand(await saveBrand({ ...form, ...extra })); toast.success("Brand saved"); }
    catch (e) { toast.error(errText(e, "Could not save the brand")); }
    finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("The logo must be under 2 MB"); return; }
    const data = await readAsBase64(file);
    await save({ logo_base64: data });
    setSuggested(await suggestPalette(`data:${file.type};base64,${data}`));
    if (fileRef.current) fileRef.current.value = "";
  };

  return <fieldset className="key-group" data-testid="settings-brand">
    <legend>Company brand</legend>
    <div className="brand-preview" style={{ background: form.primary_color, color: onColor(form.primary_color) }} data-testid="brand-preview">
      {brand.has_logo ? <img src={`${LOGO_URL}?v=${brand.logo_v || 0}`} alt="" /> : <b>{form.company_name || "Your company"}</b>}
      <span style={{ background: form.accent_color, color: onColor(form.accent_color) }}>Button</span>
    </div>
    {suggested.length > 0 && isAdmin && <div className="palette-row" data-testid="brand-suggested">
      <small>Colours found in your logo. Click one for the main colour, or a second for the accent:</small>
      <div>{suggested.map((c, i) => <button key={c} className="swatch" style={{ background: c }} title={c}
        onClick={() => setForm(f => i === 0 || f.primary_color === c ? { ...f, primary_color: c } : { ...f, accent_color: c })} />)}
        {suggested.length > 1 && <button className="outline-button small" onClick={() => setForm(f => ({ ...f, primary_color: suggested[0], accent_color: suggested[1] }))}>Use the first two</button>}</div>
    </div>}
    {contrast(form.primary_color, onColor(form.primary_color)) < 4.5 && <small className="ai-warn" data-testid="brand-contrast-warning">This main colour is too light or too mid-tone for easy-to-read text. Pick a darker or lighter one.</small>}
    <label>Company name<input value={form.company_name} disabled={!isAdmin} onChange={e => setForm({ ...form, company_name: e.target.value })} data-testid="brand-name" /></label>
    <label>Website (shown on social images)<input value={form.website} disabled={!isAdmin} placeholder="example.com" onChange={e => setForm({ ...form, website: e.target.value })} data-testid="brand-website" /></label>
    <div className="two-col">
      <label>Main colour<input type="color" value={form.primary_color} disabled={!isAdmin} onChange={e => setForm({ ...form, primary_color: e.target.value })} data-testid="brand-primary" /></label>
      <label>Accent colour<input type="color" value={form.accent_color} disabled={!isAdmin} onChange={e => setForm({ ...form, accent_color: e.target.value })} data-testid="brand-accent" /></label>
    </div>
    {isAdmin ? <div className="modal-actions" style={{ justifyContent: "flex-start", gap: 8, flexWrap: "wrap" }}>
      <button className="outline-button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="brand-logo-upload"><ImagePlus size={14} /> {brand.has_logo ? "Replace logo" : "Upload logo"}</button>
      {brand.has_logo && <button className="outline-button" onClick={() => save({ remove_logo: true })} disabled={busy}><Trash2 size={14} /> Remove logo</button>}
      <button className="primary-button" onClick={() => save()} disabled={busy} data-testid="brand-save">{busy ? "Saving…" : "Save brand"}</button>
    </div> : <small className="muted">Only an admin can change the brand.</small>}
    <small className="muted">PNG, JPG or SVG, under 2 MB. Social images need a PNG or JPG logo; an SVG logo shows in the app only.</small>
    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" hidden onChange={e => upload(e.target.files?.[0])} data-testid="brand-logo-input" />
  </fieldset>;
}
